import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackingAPI } from '../lib/api';
import TrackingTimeline from '../components/TrackingTimeline';
import OrderJourney from '../components/OrderJourney';
import { Search, Package, Truck, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Tracking() {
    const [searchParams] = useSearchParams();
    const [trackingNumber, setTrackingNumber] = useState(searchParams.get('n') || '');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [wsConnected, setWsConnected] = useState(false);
    const wsRef = useRef(null);
    const pingRef = useRef(null);

    // Connect WebSocket for live updates
    useEffect(() => {
        if (!result?.tracking_number) return;

        const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const ws = new WebSocket(`${wsUrl}/api/ws/tracking/${result.tracking_number}`);
        wsRef.current = ws;

        ws.onopen = () => {
            setWsConnected(true);
            // Keep alive ping every 25s
            pingRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send('ping');
            }, 25000);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'tracking_update') {
                    setResult(prev => ({
                        ...prev,
                        status: data.status,
                        // Mismo motivo que en OrderDetail: si el número de guía cambió,
                        // el efecto de arriba (depende de result?.tracking_number)
                        // reconecta solo bajo el número correcto.
                        tracking_number: data.tracking_number || prev?.tracking_number,
                        carrier: data.carrier || prev?.carrier,
                        events: data.events || prev?.events,
                    }));
                    toast.success('Actualizacion de rastreo recibida');
                }
            } catch {}
        };

        ws.onclose = () => { setWsConnected(false); };
        ws.onerror = () => { setWsConnected(false); };

        return () => {
            if (pingRef.current) clearInterval(pingRef.current);
            if (ws.readyState === WebSocket.OPEN) ws.close();
        };
    }, [result?.tracking_number]);

    // La consulta no lee nada del estado: recibe el numero como argumento.
    // Asi su identidad es estable y el efecto de abajo puede depender de ella
    // sin re-dispararse en cada tecla que el usuario escribe en el input.
    const runTracking = useCallback(async (num) => {
        if (!num) return;
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const res = await trackingAPI.track(num);
            setResult(res.data);
        } catch {
            setError('No se encontró el número de rastreo');
        }
        setLoading(false);
    }, []);

    // Busqueda automatica cuando se llega con /rastreo?n=GUIA
    useEffect(() => {
        const n = searchParams.get('n');
        if (n) {
            setTrackingNumber(n);
            runTracking(n);
        }
    }, [searchParams, runTracking]);

    const handleSearch = (e) => {
        e.preventDefault();
        runTracking(trackingNumber.trim());
    };

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-8" data-testid="tracking-page">
            {/* No indexable: es una herramienta de consulta (el número que
                importa es del visitante, no contenido del sitio), mismo
                patrón que Carrito/Checkout/Cuenta. Sin este Seo, la página
                nunca había puesto su propio <title> — se quedaba con el
                genérico de la portada. */}
            <Seo title="Rastreo de Pedido | Carpepel" noindex />
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-[var(--ink)] mb-2">RASTREO</h1>
            <p className="text-sm text-[var(--ink-soft)] mb-8">Ingresa tu número de rastreo para ver el estado de tu pedido</p>

            {/* Search form */}
            <form onSubmit={handleSearch} className="flex gap-3 mb-8">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-soft)]" />
                    <Input
                        data-testid="tracking-input"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Ej. CPP-4TP9X9Q9GS"
                        className="pl-10 border-2 border-[var(--line)] focus:border-[var(--kraft-deep)] rounded-sm h-12 font-mono"
                    />
                </div>
                <Button
                    type="submit"
                    data-testid="tracking-search-btn"
                    disabled={loading}
                    className="bg-[var(--ink)] text-[var(--paper)] hover:opacity-85 rounded-sm h-12 px-8 uppercase font-bold tracking-widest text-sm"
                >
                    {loading ? 'Buscando...' : 'Rastrear'}
                </Button>
            </form>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-center gap-3 mb-6" data-testid="tracking-error">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {result && (
                <div className="animate-fadeInUp" data-testid="tracking-result">
                    {/* Summary */}
                    <div className="bg-[var(--paper-2)] border border-[var(--line)] rounded-sm p-6 mb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] font-bold">Número de rastreo</p>
                                <p className="font-mono text-lg text-[var(--ink)] font-bold">{result.tracking_number}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Live indicator */}
                                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider ${
                                    wsConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-[var(--paper)] text-[var(--ink-soft)] border border-[var(--line)]'
                                }`} data-testid="ws-status">
                                    {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                    {wsConnected ? 'En vivo' : 'Desconectado'}
                                </div>
                                <div className="flex items-center gap-2 bg-[var(--line)] px-3 py-2 rounded-sm">
                                    <Truck className="w-4 h-4 text-[var(--ink-soft)]" />
                                    <span className="text-sm font-bold text-[var(--ink)]">{result.carrier}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-orange-100 px-3 py-2 rounded-sm">
                                    <Package className="w-4 h-4 text-orange-600" />
                                    <span className="text-sm font-bold text-orange-700 uppercase">{result.status?.replace(/_/g, ' ')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Ruta del pedido: la respuesta directa a "¿y ahora qué sigue?",
                        antes del historial completo de eventos de abajo. */}
                    <div className="bg-[var(--paper-2)] border border-[var(--line)] rounded-sm p-6 mb-6" data-testid="order-journey-card">
                        <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-[var(--ink)] mb-5">Estado del Pedido</h2>
                        <OrderJourney status={result.status} />
                    </div>

                    {/* Timeline */}
                    <div className="bg-[var(--paper-2)] border border-[var(--line)] rounded-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-[var(--ink)]">
                                Historial de Eventos
                            </h2>
                            {wsConnected && (
                                <span className="flex items-center gap-1.5 text-[10px] text-green-600 font-bold uppercase tracking-wider">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                    Actualizacion en tiempo real
                                </span>
                            )}
                        </div>
                        <TrackingTimeline events={result.events || []} />
                    </div>
                </div>
            )}

            {!result && !error && (
                <div className="bg-[var(--paper)] border border-[var(--line)] rounded-sm p-8 text-center">
                    <Truck className="w-12 h-12 text-[var(--ink-dim)] mx-auto mb-4" />
                    <p className="text-sm text-[var(--ink-soft)]">Tu número de rastreo se encuentra en el correo de confirmación</p>
                    <p className="text-xs text-[var(--ink-soft)] mt-2">Si tu pedido ya se envió, puede ser el número de guía de la paquetería en vez del que te dimos al confirmar tu compra</p>
                </div>
            )}
        </div>
    );
}
